/**
 * useMeasuredHeight — track the live pixel height of a host element.
 *
 * FmStandMap (via PropertyForestMap) requires an explicit pixel height. The
 * main-map container fills its FmMapCanvas at 100%; this hook measures it so the
 * DS map fills the workspace and re-fits when the layout changes.
 */
import { useEffect, useState, type RefObject } from 'react';

export function useMeasuredHeight(
  hostRef: RefObject<HTMLElement | null>,
  fallback = 600,
  min = 320,
): number {
  const [height, setHeight] = useState(fallback);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const update = () => setHeight(Math.max(min, host.clientHeight || fallback));
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(host);
    return () => observer.disconnect();
  }, [hostRef, fallback, min]);

  return height;
}
